import React from 'react';
import { cn } from '@/lib/core/utils';
import { Card, CardContent } from '@/components/ui/card';
import { User, Clock, Coffee, CheckCircle2 } from 'lucide-react';
import type { AuthenticatedEmployee } from '@/hooks/kiosk';

interface EmployeeStatusCardProps {
  employee: AuthenticatedEmployee;
  className?: string;
}

/**
 * Displays authenticated employee's current status
 */
export function EmployeeStatusCard({ employee, className }: EmployeeStatusCardProps) {
  const getStatusConfig = () => {
    switch (employee.current_status) {
      case 'clocked_in':
        return {
          label: 'Clocked In',
          icon: CheckCircle2,
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-50 dark:bg-green-500/10',
          borderColor: 'border-green-200 dark:border-green-500/30',
        };
      case 'on_break':
        return {
          label: 'On Break',
          icon: Coffee,
          color: 'text-orange-600 dark:text-orange-400',
          bgColor: 'bg-orange-50 dark:bg-orange-500/10',
          borderColor: 'border-orange-200 dark:border-orange-500/30',
        };
      case 'not_clocked_in':
      default:
        return {
          label: 'Not Clocked In',
          icon: Clock,
          color: 'text-gray-500 dark:text-gray-400',
          bgColor: 'bg-gray-50 dark:bg-muted',
          borderColor: 'border-gray-200 dark:border-border',
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const formatTime = (isoString: string | undefined) => {
    if (!isoString) return null;
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDuration = (minutes: number | undefined) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Card className={cn('border', statusConfig.borderColor, className)}>
      <CardContent className={cn('p-6', statusConfig.bgColor)}>
        {/* Employee name and avatar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{employee.name}</h2>
            <div className={cn('flex items-center gap-2 mt-1', statusConfig.color)}>
              <StatusIcon className="w-5 h-5" />
              <span className="font-medium">{statusConfig.label}</span>
            </div>
          </div>
        </div>

        {/* Time info */}
        <div className="grid grid-cols-2 gap-4">
          {employee.current_status !== 'not_clocked_in' && (
            <>
              <div className="bg-white dark:bg-card rounded-xl p-4 border border-gray-200 dark:border-border">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Clocked in at</p>
                <p className="text-gray-900 dark:text-gray-100 text-xl font-semibold">
                  {formatTime(employee.clocked_in_at) || '--:--'}
                </p>
              </div>

              <div className="bg-white dark:bg-card rounded-xl p-4 border border-gray-200 dark:border-border">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Time worked</p>
                <p className="text-gray-900 dark:text-gray-100 text-xl font-semibold">
                  {formatDuration(
                    (employee.total_worked_today_minutes || 0) -
                    (employee.total_break_today_minutes || 0)
                  )}
                </p>
              </div>
            </>
          )}

          {employee.current_status === 'on_break' && employee.break_started_at && (
            <div className="bg-orange-50 dark:bg-orange-500/10 rounded-xl p-4 col-span-2 border border-orange-200 dark:border-orange-500/30">
              <p className="text-orange-600 dark:text-orange-400 text-sm mb-1">Break started at</p>
              <p className="text-orange-700 dark:text-orange-300 text-xl font-semibold">
                {formatTime(employee.break_started_at)}
              </p>
            </div>
          )}

          {employee.total_break_today_minutes && employee.total_break_today_minutes > 0 && (
            <div className="bg-white dark:bg-card rounded-xl p-4 col-span-2 border border-gray-200 dark:border-border">
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Total break time</p>
              <p className="text-gray-700 dark:text-gray-300 text-lg font-medium">
                {formatDuration(employee.total_break_today_minutes)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
